import { getAuthHeaders, getBaseUrl } from './apiClient';

export type StageRunRecord = {
  timestamp: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  call_count: number;
  calls?: Array<{ model: string; input_tokens: number; output_tokens: number; cost_usd: number }>;
};

export type StageRecord = {
  stage_id: string;
  status: string;
  output_key?: string;
  total_cost_usd?: number;
  runs?: StageRunRecord[];
};

export type BlogRun = {
  run_id: string;
  candidate_id: string;
  pipeline_type?: 'creation' | 'refresh';
  status: 'queued' | 'running' | 'paused' | 'blocked' | 'failed' | 'succeeded';
  current_stage_id?: string | null;
  stages?: StageRecord[];
  stage_outputs?: Record<string, any>;
  prompt_instances?: Record<string, any>;
  pipeline_mode?: 'manual' | 'headless' | null;
  pipeline_job_id?: string | null;
  paused_at_stage?: string | null;
  /** Why the run paused, when the pause is a decision (e.g. 'targeting_rejected'). */
  pause_reason?: string | null;
  pending_inputs?: string[] | null;
  stage_inputs?: Record<string, Record<string, any>>;
  skip_stages?: string[];
  last_error?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  version?: number;
};

export type EditorialPrecedent = {
  type: string;
  record_id: string;
  run_id: string;
  topic: string;
  strategist: { verdict?: string | null; reasoning?: string | null };
  human: { action: string; keyword?: string | null; angle?: string | null; rationale?: string | null; author?: string };
  target?: {
    primary_keyword?: string | null;
    seo_score?: number | null;
    aeo_score?: number | null;
    strategy?: string | null;
    content_pillar?: string | null;
    focus?: string | null;
  };
  outcome?: { status?: string; [k: string]: any } | null;
  created_at: string;
};

export const blogRunsService = {
  async list(candidateId?: string): Promise<{ runs: BlogRun[]; count: number }> {
    const qs = candidateId ? `?candidate_id=${encodeURIComponent(candidateId)}` : '';
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs${qs}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to list runs (${resp.status})`);
    return await resp.json();
  },
  async get(runId: string): Promise<BlogRun> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to get run (${resp.status})`);
    return await resp.json();
  },
  async getWithDocument(runId: string): Promise<BlogRun & { document?: Record<string, any>; document_error?: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}?include=document`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to get run with document (${resp.status})`);
    return await resp.json();
  },
  async getDocument(runId: string): Promise<Record<string, any>> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/document`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to get document (${resp.status})`);
    return await resp.json();
  },
  async updateDocument(runId: string, patch: Record<string, any>): Promise<Record<string, any>> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/document`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(patch),
    });
    if (!resp.ok) throw new Error(`Failed to update document (${resp.status})`);
    return await resp.json();
  },
  async create(candidateId: string, payload?: {
    prompt_instances?: Record<string, any>;
    pipeline_type?: 'creation' | 'refresh';
    inventory_entry?: Record<string, any>;
    performance_data?: Record<string, any>;
  }): Promise<BlogRun> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ candidate_id: candidateId, ...(payload || {}) })
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to create run: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async update(runId: string, payload: Partial<BlogRun>): Promise<BlogRun> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to update run: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async renderPrompt(runId: string, stageId: string): Promise<{
    template: string;
    rendered_prompt: string;
    variables: Record<string, any>;
    model: string;
    temperature: number | null;
    max_tokens: number | null;
    source: 'prompt_instances' | 'pipeline_config';
  }> {
    const resp = await fetch(
      `${getBaseUrl()}/secure/jobs/render-prompt/${encodeURIComponent(runId)}/${encodeURIComponent(stageId)}`,
      { method: 'POST', headers: await getAuthHeaders(), body: '{}' },
    );
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to render prompt: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async savePromptOverride(
    runId: string,
    stageId: string,
    override: { prompt_template?: string; model?: string; temperature?: number; max_tokens?: number },
  ): Promise<BlogRun> {
    // Read existing prompt_instances, merge the override, save via update
    const run = await this.get(runId);
    const existing = run.prompt_instances || {};
    const updated = {
      ...existing,
      [stageId]: {
        ...(existing[stageId] || {}),
        ...override,
      },
    };
    return this.update(runId, { prompt_instances: updated });
  },
  async clearPromptOverride(runId: string, stageId: string): Promise<BlogRun> {
    const run = await this.get(runId);
    const existing = { ...(run.prompt_instances || {}) };
    delete existing[stageId];
    return this.update(runId, { prompt_instances: existing });
  },
  async getStageRaw(runId: string, stageId: string, extractionId?: string): Promise<string> {
    const qs = extractionId ? `?extraction_id=${encodeURIComponent(extractionId)}` : '';
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/stage-outputs/${encodeURIComponent(stageId)}/raw${qs}`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
    const text = await resp.text();
    if (!resp.ok) {
      throw new Error(text || `Failed to fetch raw output (${resp.status})`);
    }
    return text;
  }
  ,
  async getStageOutput(runId: string, stageId: string, extractionId?: string): Promise<any> {
    const qs = extractionId
      ? `?extraction_id=${encodeURIComponent(extractionId)}&type=output`
      : '?type=output';
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/stage-outputs/${encodeURIComponent(stageId)}/raw${qs}`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
    const text = await resp.text();
    if (!resp.ok) {
      throw new Error(text || `Failed to fetch output (${resp.status})`);
    }
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  ,
  async getStagePrompt(runId: string, stageId: string, extractionId?: string): Promise<string> {
    const qs = extractionId
      ? `?extraction_id=${encodeURIComponent(extractionId)}&type=prompt`
      : '?type=prompt';
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/stage-outputs/${encodeURIComponent(stageId)}/raw${qs}`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
    const text = await resp.text();
    if (!resp.ok) {
      throw new Error(text || `Failed to fetch prompt (${resp.status})`);
    }
    return text;
  }
  ,
  async getStageVariables(runId: string, stageId: string, extractionId?: string): Promise<any> {
    const qs = extractionId
      ? `?extraction_id=${encodeURIComponent(extractionId)}&type=vars`
      : '?type=vars';
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/stage-outputs/${encodeURIComponent(stageId)}/raw${qs}`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
    const text = await resp.text();
    if (!resp.ok) {
      throw new Error(text || `Failed to fetch variables (${resp.status})`);
    }
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  ,
  async executePipeline(runId: string, options: { mode: 'guided' | 'headless'; from_stage_id?: string }): Promise<{ job_id: string; status: string; mode: string; run_id: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/execute`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(options),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to execute pipeline: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async resumePipeline(runId: string, input?: Record<string, any>, action?: 'retry' | 'skip'): Promise<{ job_id: string; status: string; run_id: string; resumed_from: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/resume`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ input, action }),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to resume pipeline: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async overrideTargeting(
    runId: string,
    payload: { mode: 'pick' | 'new'; keyword: string; angle?: string }
  ): Promise<{ job_id: string; status: string; run_id: string; mode: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/targeting-override`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to override targeting: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async listEditorialPrecedents(): Promise<{ precedents: EditorialPrecedent[]; count: number }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/editorial-precedents`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to load editorial memory (${resp.status})`);
    return await resp.json();
  },
  async acceptTargeting(runId: string): Promise<{ job_id: string; status: string; run_id: string; from_stage: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/targeting-accept`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: '{}',
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to accept targeting: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async dialogueTargeting(runId: string, message: string): Promise<{ job_id: string; status: string; run_id: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/runs/${encodeURIComponent(runId)}/targeting-dialogue`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ message }),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to send dialogue: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async getPipelineDefaults(): Promise<{ default_business_context: string; content_pillars: string[]; clusters?: string[]; default_image_style?: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/shared-resources/pipeline-defaults`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
    if (!resp.ok) {
      throw new Error(`Failed to fetch pipeline defaults (${resp.status})`);
    }
    return await resp.json();
  },
  async resetFromStage(runId: string, stageId: string): Promise<{ run_id: string; reset_from: string; stages_cleared: string[]; status: string }> {
    const resp = await fetch(`${getBaseUrl()}/secure/jobs/reset-from/${encodeURIComponent(runId)}/${encodeURIComponent(stageId)}`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: '{}',
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Failed to reset: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
  async getSharedBrandVoice(): Promise<string> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/shared-resources/brand-voice`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
    const text = await resp.text();
    if (!resp.ok) {
      throw new Error(text || `Failed to fetch shared brand voice (${resp.status})`);
    }
    return text;
  }
};
